import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getApiV1ExamExamidLangsQueryKey,
  getApiV1ExamGridQueryKey,
  useGetApiV1ExamExamidLangs,
  usePostApiV1Exam,
  usePostApiV1ExamExamidLangs,
  usePutApiV1ExamId,
} from '@/shared/api/generated';
import type { ExamEditorViewModel } from '@/shared/api/generated/types/ExamEditorViewModel';
import type { ExamLangListItemViewModel } from '@/shared/api/generated/types/ExamLangListItemViewModel';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

import type { ExamFormData } from '../types/exam';

/**
 * Hook que gerencia o fluxo do wizard de criação de prova.
 *
 * Orquestra a criação da prova (POST) no avanço inicial do step geral,
 * a atualização (PUT) caso o usuário retorne ao step geral e avance novamente,
 * o gerenciamento de idiomas e a ativação final.
 *
 * @returns Estado e handlers para cada etapa do wizard
 *
 * @example
 * ```tsx
 * const { saveExam, examLangs, activateExam } = useExamWizard();
 * ```
 */
export function useExamWizard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examManagement');

  const [createdExamId, setCreatedExamId] = useState<number | null>(null);

  const langsQuery = useGetApiV1ExamExamidLangs(createdExamId ?? 0, undefined, {
    query: { enabled: createdExamId !== null && createdExamId > 0 },
  });

  const createMutation = usePostApiV1Exam({
    mutation: {
      onError: () => alert({ alertType: 'warning', title: t('toast.error') }),
    },
  });

  const updateFormMutation = usePutApiV1ExamId({
    mutation: {
      onError: () => alert({ alertType: 'warning', title: t('toast.error') }),
    },
  });

  const activateMutation = usePutApiV1ExamId({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('toast.createSuccess') });
        void queryClient.invalidateQueries({ queryKey: getApiV1ExamGridQueryKey() });
        void navigate({ to: '/exams' as never });
      },
      onError: () => alert({ alertType: 'warning', title: t('toast.error') }),
    },
  });

  const addLangMutation = usePostApiV1ExamExamidLangs({
    mutation: {
      onSuccess: () => {
        if (createdExamId) {
          void queryClient.invalidateQueries({
            queryKey: getApiV1ExamExamidLangsQueryKey(createdExamId),
          });
        }
      },
      onError: () => alert({ alertType: 'warning', title: t('toast.error') }),
    },
  });

  function buildPayload(formData: ExamFormData): ExamEditorViewModel {
    return {
      title: formData.title,
      instruction: formData.instruction || null,
      disciplineId: formData.disciplineId,
      companyId: formData.companyId,
      ...(formData.examType !== null ? { examType: formData.examType } : {}),
      ...(formData.gradeCriteria !== null ? { gradeCriteria: formData.gradeCriteria } : {}),
      ...(formData.examReferenceShow !== null
        ? { examReferenceShow: formData.examReferenceShow }
        : {}),
      ...(formData.examReferenceShowPost !== null
        ? { examReferenceShowPost: formData.examReferenceShowPost }
        : {}),
      showAnswersTries: formData.showAnswersTries,
      weight: formData.weight,
      randomize: formData.randomize,
      questionsQuantity: formData.questionsQuantity,
      percentualComplexity: formData.percentualComplexity,
      recommendedToDoOnPortal: formData.recommendedToDoOnPortal,
      enableBlockUnauthorizedActions: formData.enableBlockUnauthorizedActions,
      enableSafeEvaluationMode: formData.enableSafeEvaluationMode,
      maxScreenExitsAllowed: formData.maxScreenExitsAllowed,
    };
  }

  /**
   * Cria a prova via POST na primeira vez. Se já criada, atualiza via PUT.
   *
   * @returns `true` se a operação foi bem-sucedida, `false` caso contrário.
   */
  const saveExam = useCallback(
    async (formData: ExamFormData): Promise<boolean> => {
      const payload = buildPayload(formData);

      if (createdExamId === null) {
        try {
          const data = await createMutation.mutateAsync({ data: payload });
          const id = typeof data === 'number' ? data : null;

          if (id !== null) setCreatedExamId(id);
          return id !== null;
        } catch {
          return false;
        }
      }

      try {
        await updateFormMutation.mutateAsync({ id: createdExamId, data: payload });
        return true;
      } catch {
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createdExamId, createMutation, updateFormMutation, queryClient],
  );

  /**
   * Adiciona um novo idioma à prova já criada.
   */
  const addLang = useCallback(
    (data: { cultureInfo: string; active: boolean }) => {
      if (!createdExamId) return;
      addLangMutation.mutate({
        examId: createdExamId,
        data: {
          cultureInfo: data.cultureInfo,
          active: data.active,
        },
      });
    },
    [createdExamId, addLangMutation],
  );

  /**
   * Navega para a listagem de provas sem ativar.
   */
  const saveDraft = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: getApiV1ExamGridQueryKey() });
    void navigate({ to: '/exams' as never });
  }, [queryClient, navigate]);

  /**
   * Ativa a prova via PUT /api/v1/exam/{id}.
   */
  const activateExam = useCallback(() => {
    if (!createdExamId) return;
    activateMutation.mutate({ id: createdExamId, data: {} as ExamEditorViewModel });
  }, [createdExamId, activateMutation]);

  return {
    createdExamId,
    examLangs: unwrapPagedResponse<ExamLangListItemViewModel>(langsQuery.data),
    isLoadingLangs: langsQuery.isLoading,
    isSaving: createMutation.isPending || updateFormMutation.isPending,
    isAddingLang: addLangMutation.isPending,
    isActivating: activateMutation.isPending,
    saveExam,
    addLang,
    saveDraft,
    activateExam,
  } as const;
}
